import { Box, Button, Divider, Theme, Tooltip, useMediaQuery } from '@evaneos/b2b-ui';
import cx from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import Dropzone from 'react-dropzone';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import AccessTimeOutlinedIcon from '@evaneos/b2b-ui/icons/AccessTimeOutlined';
import AttachFileIcon from '@evaneos/b2b-ui/icons/AttachFile';
import InsertDriveFileOutlinedIcon from '@evaneos/b2b-ui/icons/InsertDriveFileOutlined';
import SendIcon from '@evaneos/b2b-ui/icons/Send';
import TextFormatIcon from '@evaneos/b2b-ui/icons/TextFormat';

import { useIsMutating } from '@tanstack/react-query';

import { ERROR, PENDING, SUCCESS } from '../../../../../design-system/constants';
import { AttachFile } from '../../../../../design-system/Icons/AttachFile';
import { Done } from '../../../../../design-system/Icons/Done';
import { CircularLoader } from '../../../../../design-system/Loader/CircularLoader';
import {
    generateSendingMessageId,
    saveDraftMessage,
    sendMessageRequest,
    uploadAttachmentRequest,
} from '../../../../../resources/discussion';
import {
    getAttachments,
    getDraftMessage,
    getSendingMessageStatus,
    saveDraftMessageStatus, // AT2 EXPERIMENT
} from '../../../../../resources/discussion/selectors';
import {
    Attachment as AttachmentType,
    DraftMessageMetadata,
} from '../../../../../resources/discussion/types';
import { DossierId } from '../../../../../resources/dossier/types';
import { useAuth } from '../../../../../shared/providers/AuthProvider';
import { MessageTemplate } from '../../../../../resources/message-template/types';
import { debounce } from '../../../../../resources/pipeline/utils';
import {
    useGetTrackedTripProperties,
    useTrackEventAmplitude,
    SendMessageClickedProperties,
} from '../../../../../shared/analytics';
import { useAppDispatch, useAppSelector } from '../../../../../shared/redux';
import { usePrevious } from '../../../../../shared/hooks/usePrevious';
import { levenshteinSimilarity } from '../../../../../shared/utilities/levenshteinSimilarity';
import { GenerateAT1Alert } from '../GenerateAT1Alert';
import { useGetDossierData } from '../../../../../resources/dossier/queries';
import {
    EditorContent,
    QuillEditor,
} from '../../../../../shared/components/QuillEditor/QuillEditor';
import { useHeightObserver } from '../../../../../shared/hooks/useHeightObserver';
import { useFilteredDiscussionGroupedByDate } from '../../../../../resources/discussion/queries';
import { convertSpacesInHtml } from '../../utils/messageHtmlConverter';

import { generateSummaryMailMutationKey } from '../../../../../resources/phoning/mutations';

import { AddAttachmentsModal } from './AddAttachmentsModal';
import { Attachment } from './Attachment';
import { ScheduleMessageDialog } from './ScheduleMessageDialog';
import { AT1GenerationResponseData, fetchAT1Generation } from './fetchAT1Generation';
/* START AT2 EXPERIMENT - V2 */
import { AT2_ModalsWrapper } from './AB_Maturity_AT2/AT2_ModalsWrapper';
import { useAT2MessageCheck } from './AB_Maturity_AT2/hook/useAT2MessageCheck';
/* END AT2 EXPERIMENT */

import s from './NewMessageBox.css';

export const tooltipSlotProps = {
    popper: {
        modifiers: [{ name: 'offset', options: { offset: [0, -10] } }], // Effectively 4px distance with source element
    },
};

function getSubmitMessageTrackingData(
    currentMessage: string,
    generateAT1Data: AT1GenerationResponseData | null,
    draftMessageMetadata?: DraftMessageMetadata,
): {
    similarityScore: number;
    messageSource: SendMessageClickedProperties['message_source'];
} {
    if (generateAT1Data) {
        return {
            similarityScore: levenshteinSimilarity(currentMessage, generateAT1Data.content || ''),
            messageSource: 'at1_suggestion',
        };
    }

    if (
        draftMessageMetadata?.origin === 'phone_call_summary' &&
        draftMessageMetadata.originalContent
    ) {
        return {
            similarityScore: levenshteinSimilarity(
                currentMessage,
                draftMessageMetadata.originalContent,
            ),
            messageSource: 'call_recap',
        };
    }

    return {
        similarityScore: -1,
        messageSource: 'manual',
    };
}

export type Props = {
    handleMessageTemplatesPanel: () => void;
    onHeightChange: () => void;
    selectedMessageTemplate: MessageTemplate | null;
    removeSelectedMessageTemplate: () => void;
    disableAutoFocus?: boolean;
};

export const NewMessageBox = (props: Props): JSX.Element => {
    const {
        selectedMessageTemplate,
        removeSelectedMessageTemplate,
        onHeightChange,
        handleMessageTemplatesPanel,
        disableAutoFocus = false,
    } = props;

    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const { trackEvent } = useTrackEventAmplitude();
    const { dossierId } = useParams<{ dossierId: DossierId }>();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

    const { isImpersonated } = useAuth();
    const { draftMessage, htmlDraftMessage, draftMessageMetadata } = useAppSelector(
        getDraftMessage(dossierId),
    );
    const saveDraftStatus = useAppSelector(saveDraftMessageStatus(dossierId));
    const sendingMessageStatus = useAppSelector(getSendingMessageStatus(dossierId));
    const getAttachmentsBySendingMessageId = useAppSelector(getAttachments(dossierId));
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const discussion = useFilteredDiscussionGroupedByDate(dossierId);

    const { participants } = useGetDossierData(dossierId) || {};

    const isGeneratingSummaryMailForDossier =
        useIsMutating({ mutationKey: generateSummaryMailMutationKey(dossierId) }) > 0;

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const editorContainerRef = useHeightObserver<HTMLElement>(onHeightChange);
    const uploadAttachmentInputRef = useRef<HTMLInputElement>(null);
    const draftMessageMetadataRef = useRef(draftMessageMetadata);

    const [content, setContent] = useState<EditorContent | null>({
        text: draftMessage,
        html: htmlDraftMessage,
    });
    const [files, setFiles] = useState<Array<File>>([]);
    const [sendingMessageId, setSendingMessageId] = useState<string>(generateSendingMessageId());
    const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState<boolean>(false);
    const [generateAT1Data, setGenerateAT1Data] = useState<AT1GenerationResponseData | null>(null);
    const [generateAT1Status, setGenerateAT1Status] = useState<
        'idle' | 'loading' | 'success' | 'error'
    >('idle');
    const [showToolbar, setShowToolbar] = useState<boolean>(true);
    /* START AT2 EXPERIMENT - V2 */
    const {
        handleAT2Flow,
        handleSendImprovedMessage,
        handleSendOriginalMessage,
        at2State,
        modalState,
        closeComparisonModal,
    } = useAT2MessageCheck(dossierId, sendingMessageId, {
        onSendMessage: (dossierId, sendingMessageId, message) =>
            dispatch(sendMessageRequest(dossierId, sendingMessageId, message)),
        onOpenScheduleDialog: (message: string) => {
            setContent({ text: message, html: convertSpacesInHtml(message) });
            setIsScheduleDialogOpen(true);
        },
        setSendingMessageId,
        setContent,
    });
    /* END AT2 EXPERIMENT */

    const previousSelectedMessageTemplate = usePrevious(selectedMessageTemplate);

    useEffect(() => {
        draftMessageMetadataRef.current = draftMessageMetadata;
    }, [draftMessageMetadata]);

    const hasNoMessageInTheDiscussion =
        Object.keys(discussion?.messages || {}).length === 0 && !discussion?.sendingMessage;
    const attachments = getAttachmentsBySendingMessageId(sendingMessageId);
    const hasNoMessage = content === null || content.text.trim() === '';
    const messageIsNotSending =
        !sendingMessageStatus ||
        !sendingMessageStatus[sendingMessageId] ||
        (!!sendingMessageStatus && sendingMessageStatus[sendingMessageId]?.postContent !== PENDING);

    const isSchedulingDisabled = hasNoMessage || !!discussion?.scheduledMessage;

    const debouncedSaveDraft = useMemo(
        () =>
            debounce(
                1000,
                (
                    id: DossierId,
                    draftMessage: string,
                    htmlDraftMessage: string,
                    metadata?: typeof draftMessageMetadata,
                ) => {
                    messageIsNotSending &&
                        dispatch(saveDraftMessage(id, draftMessage, htmlDraftMessage, metadata));
                },
            ),
        [dispatch, messageIsNotSending],
    );

    useEffect(() => {
        if (selectedMessageTemplate === null) {
            return;
        }
        if (previousSelectedMessageTemplate !== selectedMessageTemplate) {
            setContent({
                text: selectedMessageTemplate.content,
                html: convertSpacesInHtml(selectedMessageTemplate.content),
            });

            removeSelectedMessageTemplate();
        }
    }, [previousSelectedMessageTemplate, removeSelectedMessageTemplate, selectedMessageTemplate]);

    // override the message box content if there's a draft from phone call summary
    useEffect(() => {
        if (draftMessageMetadata?.origin !== 'phone_call_summary') return;

        setContent({ text: draftMessage, html: htmlDraftMessage });
    }, [draftMessage, htmlDraftMessage, draftMessageMetadata?.origin]);

    useEffect(() => {
        const textAreaScrollDown = () => {
            if (textareaRef.current) {
                textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
            }
        };
        textAreaScrollDown();
    }, []);

    function handleChangeEnrichText(content: EditorContent): void {
        setContent(content);

        if (isImpersonated) {
            return;
        }

        debouncedSaveDraft(dossierId, content.text, content.html, draftMessageMetadataRef.current);
    }

    async function handleSubmit(): Promise<void> {
        // Track message send event
        const currentMessage = content?.text ?? '';
        const { similarityScore, messageSource } = getSubmitMessageTrackingData(
            currentMessage,
            generateAT1Data,
            draftMessageMetadataRef.current,
        );

        trackEvent((ampli, defaultProperties) => {
            ampli.sendMessageClicked({
                ...defaultProperties,
                ...trackedTripProperties,
                similarity_score: similarityScore,
                is_at1: !!generateAT1Data,
                message_source: messageSource,
            });
        });

        /* START AT2 EXPERIMENT - V2 FLOW COMPLET */
        const at2Result = await handleAT2Flow(currentMessage, 'send');

        // Si AT2 a arrêté le flow (besoin d'action utilisateur dans le modal)
        if (at2Result.shouldStopFlow) {
            return; // STOP - attendre action utilisateur dans le modal de comparaison
        }
        /* END AT2 EXPERIMENT */

        if (content) {
            dispatch(sendMessageRequest(dossierId, sendingMessageId, content.text, content.html));
            setContent(null);
        }
        setSendingMessageId(generateSendingMessageId());

        if (generateAT1Data) {
            setGenerateAT1Data(null);
        }
    }

    function handleGenerateAT1AlertClick(): void {
        setGenerateAT1Status('loading');
        trackEvent((ampli, defaultProperties) => {
            ampli.generateAt1MessageClicked({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
        void fetchAT1Generation(Number(dossierId))
            .then((at1GenerationDataFromFetch) => {
                if (at1GenerationDataFromFetch?.content) {
                    setContent({
                        text: at1GenerationDataFromFetch.content,
                        html: convertSpacesInHtml(at1GenerationDataFromFetch.content),
                    });
                    setGenerateAT1Data(at1GenerationDataFromFetch);
                    setGenerateAT1Status('success');
                    dispatch(
                        saveDraftMessage(
                            dossierId,
                            at1GenerationDataFromFetch.content,
                            convertSpacesInHtml(at1GenerationDataFromFetch.content),
                        ),
                    );
                }
            })
            .catch(() => {
                setGenerateAT1Data(null);
                setGenerateAT1Status('error');
            });
    }

    // Move into own attachment container
    function selectAttachments(files: File[]): void {
        trackEvent((ampli, defaultProperties) => {
            ampli.discussionAddADocumentClicked({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
        setFiles([...files]);
    }

    function uploadAttachments(attachments: Array<AttachmentType>): void {
        attachments.forEach((attachment) =>
            dispatch(
                uploadAttachmentRequest(
                    attachment.file,
                    dossierId,
                    sendingMessageId,
                    attachment.category,
                ),
            ),
        );
    }

    function handleClickAddTemplate(): void {
        handleMessageTemplatesPanel();
        trackEvent((ampli, defaultProperties) => {
            ampli.discussionAddATemplateClicked({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
    }

    async function handleOpenScheduleDialog() {
        trackEvent((ampli, defaultProperties) => {
            ampli.scheduledMessageOpenModal({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });

        /* START AT2 EXPERIMENT - V2 FLOW PROGRAMMATION */
        const at2Result = await handleAT2Flow(content?.text ?? '', 'schedule');

        // Si AT2 a arrêté le flow (besoin d'action utilisateur dans le modal)
        if (at2Result.shouldStopFlow) {
            return; // STOP - attendre action utilisateur dans le modal de comparaison
        }
        /* END AT2 EXPERIMENT */

        // Si AT2 valide le message ou pas de vérification, ouvrir directement le dialog de programmation
        setIsScheduleDialogOpen(true);
    }
    function handleScheduleMessageSuccess(): void {
        setIsScheduleDialogOpen(false);
        setContent(null);
    }

    return (
        <>
            <Dropzone onDrop={selectAttachments} maxSize={32000000} noClick>
                {({ getRootProps, getInputProps, isDragActive }) => (
                    <Box
                        sx={{
                            padding: {
                                xs: '4px 16px 0 16px',
                                lg: '4px 24px 0 24px',
                            },
                            position: 'relative',
                        }}
                        {...getRootProps()}
                    >
                        <AddAttachmentsModal
                            key={files.length}
                            inputs={files}
                            isOpen={files.length > 0}
                            dossierId={dossierId}
                            onClose={() => setFiles([])}
                            onSend={(attachments) => {
                                uploadAttachments(attachments);
                                setFiles([]);
                            }}
                        />
                        {participants && hasNoMessageInTheDiscussion && (
                            <GenerateAT1Alert
                                participants={participants}
                                status={generateAT1Status}
                                onClick={handleGenerateAT1AlertClick}
                            />
                        )}
                        <Box
                            ref={editorContainerRef}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                border: '1px solid',
                                borderColor: 'border.main',
                                borderRadius: '4px',
                                width: '100%',
                            }}
                        >
                            <QuillEditor
                                value={content?.html ?? ''}
                                onTextChange={handleChangeEnrichText}
                                autoFocus={!disableAutoFocus}
                                className={cx('quill-editor-container', s.enrichTextarea)}
                                readOnly={generateAT1Status === 'loading'}
                                placeholder={formatMessage({
                                    id: 'discussion.input_placeholder',
                                })}
                                showToolbar={showToolbar}
                            />

                            {attachments.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {Object.values(attachments).map((attachment) => (
                                        <Attachment
                                            attachment={attachment}
                                            key={attachment.file.name}
                                            sendingMessageId={sendingMessageId}
                                            retryUploadAttachment={(attachment) =>
                                                uploadAttachments([
                                                    {
                                                        file: attachment.file,
                                                        category: attachment.category,
                                                    },
                                                ])
                                            }
                                            {...props}
                                        />
                                    ))}
                                </Box>
                            )}

                            <Box
                                sx={{
                                    display: 'flex',
                                    padding: 1.5,
                                    gap: 1,
                                    '& svg': { fontSize: '2.4rem' },
                                }}
                            >
                                <Tooltip
                                    title={formatMessage({ id: 'discussion.add_attachments' })}
                                    placement="top"
                                    disableInteractive
                                    slotProps={tooltipSlotProps}
                                >
                                    <Button
                                        onClick={() => uploadAttachmentInputRef.current?.click()}
                                        variant="text"
                                        color="secondary"
                                        sx={{ width: '40px', minWidth: '40px' }}
                                    >
                                        <input
                                            {...getInputProps()}
                                            ref={uploadAttachmentInputRef}
                                            data-testid="upload-attachment-input"
                                            style={{ display: 'none' }}
                                        />
                                        <AttachFileIcon />
                                    </Button>
                                </Tooltip>
                                <Tooltip
                                    title={formatMessage({ id: 'discussion.add_template' })}
                                    placement="top"
                                    disableInteractive
                                    slotProps={tooltipSlotProps}
                                >
                                    <Button
                                        onClick={handleClickAddTemplate}
                                        variant="text"
                                        color="secondary"
                                        sx={{ width: '40px', minWidth: '40px' }}
                                    >
                                        <InsertDriveFileOutlinedIcon />
                                    </Button>
                                </Tooltip>

                                <Divider orientation="vertical" flexItem />
                                <Tooltip
                                    title={formatMessage({
                                        id: showToolbar
                                            ? 'discussion.editor.hide_format_toolbar'
                                            : 'discussion.editor.show_format_toolbar',
                                    })}
                                    placement="top"
                                    disableInteractive
                                    slotProps={tooltipSlotProps}
                                >
                                    <Button
                                        onClick={() => setShowToolbar(!showToolbar)}
                                        variant="text"
                                        color="secondary"
                                        sx={{ width: '40px', minWidth: '40px' }}
                                        aria-label={formatMessage({
                                            id: showToolbar
                                                ? 'discussion.editor.hide_format_toolbar'
                                                : 'discussion.editor.show_format_toolbar',
                                        })}
                                    >
                                        <TextFormatIcon />
                                    </Button>
                                </Tooltip>

                                <Box flex={1} />

                                <Tooltip
                                    title={formatMessage({
                                        id: 'discussion.schedule_messages.modal.submit',
                                    })}
                                    placement="top"
                                    disableInteractive
                                    slotProps={tooltipSlotProps}
                                >
                                    <Button
                                        disabled={isSchedulingDisabled}
                                        onClick={() => void handleOpenScheduleDialog()}
                                        variant={isSchedulingDisabled ? 'contained' : 'outlined'}
                                        color="secondary"
                                        size="medium"
                                        sx={{
                                            width: '40px',
                                            height: '40px',
                                            minWidth: '40px',
                                            transitionProperty: 'background-color, color',
                                        }}
                                    >
                                        <AccessTimeOutlinedIcon />
                                    </Button>
                                </Tooltip>
                                <Button
                                    onClick={() => void handleSubmit()}
                                    disabled={
                                        hasNoMessage ||
                                        generateAT1Status === 'loading' ||
                                        isGeneratingSummaryMailForDossier
                                    }
                                    data-testid="send-button"
                                    sx={{
                                        height: '40px',
                                        ...(isMobile && { width: '40px', minWidth: '40px' }),
                                    }}
                                >
                                    {isMobile ? <SendIcon /> : formatMessage({ id: 'global.send' })}
                                </Button>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                height: '20px',
                                marginY: '4px',
                            }}
                        >
                            {saveDraftStatus === PENDING && (
                                <div className={s.saving}>
                                    <CircularLoader
                                        className={s.draftLoader}
                                        color={'var(--ink)'}
                                        thickness={3}
                                    />
                                    {formatMessage({ id: 'global.saving' })}
                                    {'...'}
                                </div>
                            )}
                            {saveDraftStatus === SUCCESS && (
                                <span className={s.saved} data-testid="draft-saved">
                                    <Done color="var(--ink)" />
                                    {formatMessage({ id: 'global.saved' })}
                                </span>
                            )}
                            {saveDraftStatus === ERROR && (
                                <p className={s.error}>
                                    {formatMessage({
                                        id: 'discussion.draft_message.saving_error',
                                    }) + ' '}
                                    <a
                                        onClick={() => {
                                            dispatch(
                                                saveDraftMessage(
                                                    dossierId,
                                                    content?.text ?? '',
                                                    content?.html ?? '',
                                                    draftMessageMetadataRef.current,
                                                ),
                                            );
                                        }}
                                        className={s.retry}
                                        data-testid="draft-retry"
                                    >
                                        {formatMessage({
                                            id: 'global.retry',
                                        })}
                                    </a>
                                </p>
                            )}
                        </Box>

                        {isDragActive && (
                            <div className={s.dragOverlay}>
                                <AttachFile color="var(--ink-light)" width="20px" height="20px" />
                                {formatMessage({ id: 'discussion.drop_file' })}
                            </div>
                        )}
                    </Box>
                )}
            </Dropzone>

            <ScheduleMessageDialog
                open={isScheduleDialogOpen}
                onClose={() => setIsScheduleDialogOpen(false)}
                onSuccess={handleScheduleMessageSuccess}
                editorContent={content}
                sendingMessageId={sendingMessageId}
            />

            {/* START AT2 EXPERIMENT - V2 FLOW COMPLET */}
            <AT2_ModalsWrapper
                at2State={at2State}
                modalState={modalState}
                onSendImproved={handleSendImprovedMessage}
                onSendOriginal={handleSendOriginalMessage}
                onClose={closeComparisonModal}
            />
            {/* END AT2 EXPERIMENT */}
        </>
    );
};
